const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

export type GlobalSearchType = "all" | "posts" | "people" | "spaces" | "notes" | "tags";
export type GlobalSearchDate = "all" | "24h" | "7d" | "30d";
export type GlobalSearchSort = "relevance" | "newest" | "popular";
export type GlobalSearchNoteScope = "accessible" | "owned" | "shared";
export type GlobalSearchSpaceAccess = "visible" | "open" | "joined";

export type GlobalSearchItemKind = "post" | "person" | "space" | "note" | "tag";

export type GlobalSearchItem = {
    kind: GlobalSearchItemKind;
    id: string;
    score?: number;
    title: string;
    subtitle?: string;
    content?: string;
    href?: string;
    sortDate?: string;
    createdAt?: string;
    updatedAt?: string;
    username?: string;
    displayName?: string;
    avatarUrl?: string;
    author?: any;
    space?: any;
    reactions?: any;
    visibility?: string;
    desc?: string;
    by?: string;
    members?: string;
    category?: string;
    accentColor?: string;
    accessMode?: string;
    archivedAt?: string | null;
    type?: string;
    color?: string;
    tags?: string[];
    folder?: { id: string; name: string; color?: string } | null;
    isOwner?: boolean;
    wordCount?: number;
    tag?: string;
    source?: string;
    count?: number;
};

export type GlobalSearchGroups = {
    posts: GlobalSearchItem[];
    people: GlobalSearchItem[];
    spaces: GlobalSearchItem[];
    notes: GlobalSearchItem[];
    tags: GlobalSearchItem[];
};

export type GlobalSearchResponse = {
    success: boolean;
    query?: Record<string, unknown>;
    items: GlobalSearchItem[];
    groups: GlobalSearchGroups;
    totals: {
        all: number;
        posts: number;
        people: number;
        spaces: number;
        notes: number;
        tags: number;
    };
    page: number;
    limit: number;
    hasMore: boolean;
    nextPage: number | null;
    message?: string;
};

export type FetchGlobalSearchParams = {
    q: string;
    type?: GlobalSearchType;
    date?: GlobalSearchDate;
    sort?: GlobalSearchSort;
    noteScope?: GlobalSearchNoteScope;
    spaceAccess?: GlobalSearchSpaceAccess;
    page?: number;
    limit?: number;
    signal?: AbortSignal;
};

export async function fetchGlobalSearch(params: FetchGlobalSearchParams): Promise<GlobalSearchResponse> {
    const url = new URL(`${API_BASE}/api/search`);
    url.searchParams.set("q", params.q);
    url.searchParams.set("type", params.type || "all");
    url.searchParams.set("date", params.date || "all");
    url.searchParams.set("sort", params.sort || "relevance");
    url.searchParams.set("noteScope", params.noteScope || "accessible");
    url.searchParams.set("spaceAccess", params.spaceAccess || "visible");
    url.searchParams.set("page", String(params.page || 1));
    url.searchParams.set("limit", String(params.limit || 18));

    const res = await fetch(url.toString(), {
        headers: authHeaders(),
        signal: params.signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || `Search failed: ${res.status}`);
    }
    return {
        success: Boolean(data.success),
        query: data.query || {},
        items: data.items || [],
        groups: {
            posts: data.groups?.posts || [],
            people: data.groups?.people || [],
            spaces: data.groups?.spaces || [],
            notes: data.groups?.notes || [],
            tags: data.groups?.tags || [],
        },
        totals: {
            all: data.totals?.all || 0,
            posts: data.totals?.posts || 0,
            people: data.totals?.people || 0,
            spaces: data.totals?.spaces || 0,
            notes: data.totals?.notes || 0,
            tags: data.totals?.tags || 0,
        },
        page: data.page || 1,
        limit: data.limit || params.limit || 18,
        hasMore: Boolean(data.hasMore),
        nextPage: data.nextPage || null,
        message: data.message,
    };
}
